import { Granularity } from '../types';

/**
 * ADR 0010 paragraph-boundary contract, matching
 * `ks2_marking/src/preprocessing.py:_normalise_paragraphs`.
 *
 * `||` aliases `\n\n`, standalone `//` (not preceded by `:`, so `https://`
 * stays intact) also aliases `\n\n`, `\\` is not an alias, runs of three or
 * more newlines collapse, splitting is on `\n\n`, and empty or
 * whitespace-only blocks are dropped. Internal single newlines and
 * leading/trailing whitespace on kept blocks are preserved.
 */
export const normaliseParagraphs = (content: string): string[] => {
  const normalised = content
    .replace(/\|\|/g, '\n\n')
    .replace(/(?<!:)\/\//g, '\n\n')
    .replace(/\n{3,}/g, '\n\n');
  return normalised.split('\n\n').filter(paragraph => paragraph.trim().length > 0);
};

export const useEssayUtils = () => {
  // Split essay content into sentences, preserving paragraph breaks
  const getSentences = (content: string, clean: boolean = false): string[] => {
    const paragraphs = normaliseParagraphs(content);

    // Process each paragraph separately
    const allSentences: string[] = [];

    paragraphs.forEach((paragraph, paragraphIndex) => {
      // Split paragraph into sentences
      const paragraphSentences = paragraph.split(/(?<=[.!?]["']?)\s+/);

      // Add each sentence, marking paragraph breaks with a special prefix
      paragraphSentences.forEach((sentence, sentenceIndex) => {
        if (sentenceIndex === 0 && paragraphIndex > 0) {
          // This is the first sentence of a non-first paragraph, add a paragraph break prefix
          allSentences.push(`\n\n${sentence}`);
        } else {
          allSentences.push(sentence);
        }
      });
    });

    if (clean) {
      return allSentences.map(cleanSentenceText);
    }

    return allSentences;
  };

  const getFragments = (
    content: string,
    granularity: Granularity,
    clean: boolean = false
  ): string[] => {
    if (granularity === 'essay') {
      return [content.trim()];
    }

    if (granularity === 'paragraph') {
      const paragraphs = normaliseParagraphs(content);
      if (clean) {
        return paragraphs.map(p => p.trim());
      }
      return paragraphs.map((p, i) => (i > 0 ? `\n\n${p}` : p));
    }

    return getSentences(content, clean);
  };

  // Clean sentence text (e.g., remove paragraph breaks)
  const cleanSentenceText = (text: string): string => {
    return text.startsWith('\n\n') ? text.substring(2) : text;
  };

  return {
    getSentences,
    getFragments,
    cleanSentenceText,
  };
};
